/**
 * 2D Command Parser
 * 
 * Parses command line input and executes corresponding actions.
 * Replicates 2D's command-line interface.
 */

export interface CommandResult {
    success: boolean;
    message: string;
    action?: string;
    params?: Record<string, unknown>;
}

/**
 * Command definitions
 */
const COMMANDS: Record<string, {
    aliases: string[];
    description: string;
    tool?: string;
    handler?: (args: string[]) => CommandResult;
}> = {
    LINE: {
        aliases: ['L'],
        description: 'Draw lines',
        tool: 'line',
    },
    CIRCLE: {
        aliases: ['C'],
        description: 'Draw a circle',
        tool: 'circle',
    },
    ARC: {
        aliases: ['A'],
        description: 'Draw an arc',
        tool: 'arc',
    },
    POLYGON: {
        aliases: ['POL', 'PG'],
        description: 'Draw a regular polygon',
        tool: 'polygon',
    },
    ELLIPSE: {
        aliases: ['EL'],
        description: 'Draw an ellipse',
        tool: 'ellipse',
    },
    TEXT: {
        aliases: ['T', 'DT'],
        description: 'Create single-line text',
        tool: 'text',
    },
    MTEXT: {
        aliases: ['MT', 'T'],
        description: 'Create multi-line text',
        tool: 'mtext',
    },
    DIMLINEAR: {
        aliases: ['DLI', 'DIMLIN'],
        description: 'Create linear dimension',
        tool: 'dimLinear',
    },
    DIMALIGNED: {
        aliases: ['DAL', 'DIMAL'],
        description: 'Create aligned dimension',
        tool: 'dimAligned',
    },
    DIMRADIUS: {
        aliases: ['DRA', 'DIMRAD'],
        description: 'Create radius dimension',
        tool: 'dimRadius',
    },
    DIMDIAMETER: {
        aliases: ['DDI', 'DIMDIA'],
        description: 'Create diameter dimension',
        tool: 'dimDiameter',
    },
    // BIM Commands
    WALL: {
        aliases: ['WA'],
        description: 'Draw a wall',
        tool: 'wall',
    },
    DOOR: {
        aliases: ['DR'],
        description: 'Place a door',
        tool: 'door',
    },
    WINDOW: {
        aliases: ['WN'],
        description: 'Place a window',
        tool: 'window',
    },
    FLOOR: {
        aliases: ['FL'],
        description: 'Create a floor slab',
        tool: 'floor',
    },
    COLUMN: {
        aliases: ['CL', 'COL'],
        description: 'Place a column',
        tool: 'column',
    },
    // NURBS Commands
    LOFT: {
        aliases: [],
        description: 'Create loft surface from curves',
        tool: 'loft',
    },
    SWEEP: {
        aliases: ['SW'],
        description: 'Sweep profile along rail',
        tool: 'sweep',
    },
    REVOLVE: {
        aliases: ['REV'],
        description: 'Revolve profile around axis',
        tool: 'revolve',
    },
    EXTRUDE: {
        aliases: ['EXT'],
        description: 'Extrude curve to surface/solid',
        tool: 'extrude',
    },
    UNION: {
        aliases: ['UNI'],
        description: 'Boolean Union',
        tool: 'booleanUnion',
    },
    SUBTRACT: {
        aliases: ['SU', 'SUB'],
        description: 'Boolean Difference',
        tool: 'booleanDifference',
    },
    INTERSECT: {
        aliases: ['IN', 'INT'],
        description: 'Boolean Intersection',
        tool: 'booleanIntersection',
    },
    RECTANGLE: {
        aliases: ['REC', 'RECTANG'],
        description: 'Draw a rectangle',
        tool: 'rectangle',
    },
    POLYLINE: {
        aliases: ['PL', 'PLINE'],
        description: 'Draw a polyline',
        tool: 'polyline',
    },
    ERASE: {
        aliases: ['E', 'DEL', 'DELETE'],
        description: 'Erase selected objects',
        handler: () => ({ success: true, message: 'Select objects to erase', action: 'erase' }),
    },
    MOVE: {
        aliases: ['M'],
        description: 'Move selected objects',
        handler: () => ({ success: true, message: 'Select objects to move', action: 'move' }),
    },
    COPY: {
        aliases: ['CO', 'CP'],
        description: 'Copy selected objects',
        handler: () => ({ success: true, message: 'Select objects to copy', action: 'copy' }),
    },
    ROTATE: {
        aliases: ['RO'],
        description: 'Rotate selected objects',
        handler: () => ({ success: true, message: 'Select objects to rotate', action: 'rotate' }),
    },
    SCALE: {
        aliases: ['SC'],
        description: 'Scale selected objects',
        handler: () => ({ success: true, message: 'Select objects to scale', action: 'scale' }),
    },
    MIRROR: {
        aliases: ['MI'],
        description: 'Mirror selected objects',
        handler: () => ({ success: true, message: 'Select objects to mirror', action: 'mirror' }),
    },
    OFFSET: {
        aliases: ['O'],
        description: 'Create parallel copies',
        handler: () => ({ success: true, message: 'Specify offset distance', action: 'offset' }),
    },
    TRIM: {
        aliases: ['TR'],
        description: 'Trim objects',
        handler: () => ({ success: true, message: 'Select cutting edges', action: 'trim' }),
    },
    EXTEND: {
        aliases: ['EX'],
        description: 'Extend objects',
        handler: () => ({ success: true, message: 'Select boundary edges', action: 'extend' }),
    },
    FILLET: {
        aliases: ['F'],
        description: 'Create rounded corners',
        handler: () => ({ success: true, message: 'Specify fillet radius', action: 'fillet' }),
    },
    CHAMFER: {
        aliases: ['CHA'],
        description: 'Create beveled corners',
        handler: () => ({ success: true, message: 'Specify chamfer distances', action: 'chamfer' }),
    },
    ZOOM: {
        aliases: ['Z'],
        description: 'Zoom view',
        handler: (args) => {
            const mode = args[0]?.toUpperCase() || 'E';
            const modes: Record<string, string> = {
                'E': 'Zoom Extents',
                'A': 'Zoom All',
                'W': 'Zoom Window',
                'P': 'Zoom Previous',
                'S': 'Zoom Scale',
                'O': 'Zoom Object (selection)',
            };
            return {
                success: true,
                message: modes[mode] || 'Zoom',
                action: 'zoom',
                params: { mode, scale: args[1] }
            };
        },
    },
    PAN: {
        aliases: ['P'],
        description: 'Pan view',
        handler: (args) => {
            // PAN dx,dy (screen pixels) — a plain PAN keeps the legacy tool id.
            if (args[0]) {
                const [dx, dy] = String(args[0]).split(',').map(Number);
                return {
                    success: true,
                    message: 'Pan',
                    action: 'pan',
                    params: { dx, dy },
                };
            }
            return { success: true, message: 'Pan view', action: 'activateTool', params: { tool: 'pan' } };
        },
    },
    ORBIT: {
        aliases: ['3DO', '3DORBIT'],
        description: '3D Orbit view',
        tool: 'orbit',
    },
    UNDO: {
        aliases: ['U'],
        description: 'Undo last action',
        handler: () => ({ success: true, message: 'Undo', action: 'undo' }),
    },
    REDO: {
        aliases: [],
        description: 'Redo last undone action',
        handler: () => ({ success: true, message: 'Redo', action: 'redo' }),
    },
    LAYER: {
        aliases: ['LA'],
        description: 'Open Layer Manager',
        handler: () => ({ success: true, message: 'Layer Manager', action: 'layer' }),
    },
    PROPERTIES: {
        aliases: ['PR', 'PROPS'],
        description: 'Open Properties panel',
        handler: () => ({ success: true, message: 'Properties', action: 'properties' }),
    },
    HELP: {
        aliases: ['?', 'H'],
        description: 'Show help',
        handler: () => ({
            success: true,
            message: 'Available commands: ' + Object.keys(COMMANDS).join(', '),
            action: 'help'
        }),
    },
};

/**
 * Parse and execute a command string
 */
export function parseCommand(input: string): CommandResult {
    const trimmed = input.trim().toUpperCase();

    if (!trimmed) {
        return { success: false, message: '' };
    }

    // Split into command and arguments
    const parts = trimmed.split(/\s+/);
    const cmdName = parts[0];
    const args = parts.slice(1);

    // Find matching command
    for (const [name, cmd] of Object.entries(COMMANDS)) {
        if (name === cmdName || cmd.aliases.includes(cmdName)) {
            // If it has a custom handler, execute it
            if (cmd.handler) {
                return cmd.handler(args);
            }

            // If it activates a tool, return tool activation
            if (cmd.tool) {
                return {
                    success: true,
                    message: cmd.description,
                    action: 'activateTool',
                    params: { tool: cmd.tool },
                };
            }

            return { success: true, message: cmd.description };
        }
    }

    // Point / distance / polar entry: "5,3", "5,3,4", "@2,4", "5<30", "@5<30", "5".
    // The raw token is passed to the engine (resolveDynamicPoint) which
    // turns it into an exact point for the active command — the same
    // grammar the cursor Dynamic Input tooltip uses.
    if (/^@?-?\d*\.?\d+(,-?\d*\.?\d+){0,2}(<-?\d*\.?\d+)?$/.test(cmdName) && /\d/.test(cmdName)) {
        return {
            success: true,
            message: `Point: ${cmdName}`,
            action: 'point',
            params: { input: cmdName },
        };
    }

    // Unknown command
    return {
        success: false,
        message: `Unknown command: ${cmdName}`,
    };
}

/**
 * Get command suggestions for autocomplete
 */
export function getCommandSuggestions(partial: string): string[] {
    const upper = partial.toUpperCase();
    const suggestions: string[] = [];

    for (const [name, cmd] of Object.entries(COMMANDS)) {
        if (name.startsWith(upper)) {
            suggestions.push(name);
        }
        for (const alias of cmd.aliases) {
            if (alias.startsWith(upper)) {
                suggestions.push(`${alias} (${name})`);
            }
        }
    }

    return suggestions.slice(0, 10);
}

/**
 * Get all available commands
 */
export function getAllCommands(): { name: string; aliases: string[]; description: string }[] {
    return Object.entries(COMMANDS).map(([name, cmd]) => ({
        name,
        aliases: cmd.aliases,
        description: cmd.description,
    }));
}
